import { toast } from 'sonner'

import { useBulkCorrectPurchaseOrderStatus } from '@/api/hooks'
import { ChoicePicker } from '@/components/choice-picker'
import { STATUS_EDIT_REASON } from '@/components/manual-status-control'
import { PO_STATUS_CHOICES } from '@/components/manual-status-options'
import { LoadingButton } from '@/components/loading-button'
import { formatApiErrorMessage } from '@/lib/api-error'

export interface BulkPOTarget {
  uuid: string
  po_number: string
  status: string
  status_revision: number
}

/** "Set PO status" for the selected POs: pick a status and every one moves to it; items follow. */
export function BulkPOStatusPicker({
  pos,
  onSaved,
  variant = 'default',
}: {
  pos: BulkPOTarget[]
  onSaved?: () => void
  variant?: 'default' | 'outline'
}) {
  const mutation = useBulkCorrectPurchaseOrderStatus()
  const save = (status: string) =>
    mutation.mutate(
      {
        items: pos.map((po) => ({
          uuid: po.uuid,
          expected_revision: po.status_revision,
          expected_status: po.status,
        })),
        status,
        reason: STATUS_EDIT_REASON,
        allow_regression: true,
      },
      {
        onSuccess: (data) => {
          toast.success(`${data?.changed ?? pos.length} purchase orders updated`)
          onSaved?.()
        },
        onError: (error) => toast.error(formatApiErrorMessage('Statuses not changed', error)),
      },
    )

  return (
    <ChoicePicker
      title={pos.length === 1 ? `PO ${pos[0]?.po_number} status` : `Status for ${pos.length} POs`}
      label="Set PO status"
      value=""
      choices={PO_STATUS_CHOICES}
      pending={mutation.isPending}
      onSelect={save}
      buttonTrigger
    >
      <LoadingButton size="sm" className="h-8" variant={variant} loading={mutation.isPending} loadingText="Saving...">
        Set PO status
      </LoadingButton>
    </ChoicePicker>
  )
}
